<!-- PrescriptionDetails.vue -->
<script setup lang="ts">
    import "../../../../../css/choice.css"
    import { reactive, onBeforeMount, computed } from 'vue';
    import * as choiceApi from '../../../../lib/ChoiceApi'
    import { DatabaseId } from '../../../../lib/Models/DatabaseIdModel';
    import { DatabaseIdTypes, EnvironmentType, PhoneType } from '../../../../Enums/ChoiceEnums';    
    import { DisplayStyle, HoverCursorTypes, IconTypes, TextDisplayStyle } from '../../../../Enums/UiEnums';
    import { prettyDate, prettyTime } from '../../../../lib/DateUtils';
    import { CoverMyMedsEpaQuestionSet, CoverMyMedsEpaQuestionSetPrescriber, IEpaDetailsModel, EpaPriorityType, CoverMyMedsEpaQuestionResponse, IPrescriptionDetails } from '../../../../lib/Api/CoverMyMeds';
    import { IEntityInfoById } from '../../../../lib/Models/EntityModel';
    import { serverEnvironment } from '../../../../lib/Environment';
    import { popAlert } from '../../../../lib/Notification';
    import { useListStore } from '../../../../lib/Stores/ListStore';
    import { initPinia } from '../../../../lib/PiniaMgr';
    import { IMedicationDispenseInfoModel } from '../../../../lib/Api/Prescription';
    import TitledRow from '../../../../components/TitledRow.vue';
    import Icon from '../../../../components/Icon.vue';
    import MessageText from '../../../../components/Messages/MessageText.vue';
    import ChoicePatientName from '../../../../components/ChoicePatientName.vue';
    import MessageBadge from '../../../../components/Messages/MessageBadge.vue';
    import ToggleIcon from '../../../../components/ToggleIcon.vue';
    import EntityPhone from '../../../../components/Formatting/EntityPhone.vue'
    import OpenPatientButton from '../../../../components/Buttons/OpenPatientButton.vue';

    const listStore = useListStore(initPinia());

    const props = defineProps<{
        questionSet?: CoverMyMedsEpaQuestionSet,
        prescriber?: CoverMyMedsEpaQuestionSetPrescriber,
        taskItemIdType: DatabaseIdTypes,
        taskItemId: number
    }>();

    const compData = reactive<{
        patientEntity?: IEntityInfoById,
        dispenseInfo?: IMedicationDispenseInfoModel,
        epaDetails?: IEpaDetailsModel,
        prescriptionDetails?: IPrescriptionDetails,
        hidePatientDetails: boolean
    }>({
        hidePatientDetails: true
    });

    const prescriptionDetails = computed<IPrescriptionDetails | undefined>(()=>{
        if (compData.prescriptionDetails)
            return compData.prescriptionDetails;
        if (props.questionSet && props.prescriber)
        {
            return {
                patientId: props.questionSet.patientId,
                medication: props.questionSet.medication,
                quantity: props.questionSet.quantity,
                daysSupply: props.questionSet.daysSupply,
                prescriberFirstName: props.prescriber.firstName,
                prescriberLastName: props.prescriber.lastName,
                prescriberNpi: props.prescriber.npi,
                referenceNumber: props.questionSet.referenceNumber,
                deadline: props.questionSet.deadline
            }
        }
    });

    onBeforeMount(async () =>{
        // If questionSet or prescriber isn't available from the props, try to load it
        if ((!props.questionSet || !props.prescriber) && (DatabaseIdTypes.cmm_epa_callback_id === props.taskItemIdType || DatabaseIdTypes.cmm_epa_send_id === props.taskItemIdType))
        {
            compData.prescriptionDetails = await choiceApi.coverMyMeds.getPrescriptionDetails(props.taskItemIdType, props.taskItemId);
            if (!compData.prescriptionDetails)
            {
                await popAlert("Unable to get prescription details");
                return;
            }
        }

        if (!prescriptionDetails.value)
            return;

        compData.epaDetails = await choiceApi.coverMyMeds.getEpaDetails(props.taskItemIdType, props.taskItemId);

        // Get the patient entity info
        if (prescriptionDetails.value.patientId)
            compData.patientEntity = await choiceApi.entityInfo.getEntityInfoByDatabaseId(new DatabaseId(prescriptionDetails.value.patientId, DatabaseIdTypes.patient_id));

        // Get dosage units
        compData.dispenseInfo = await listStore.getDefaultDispense(prescriptionDetails.value.quantityUnitOfMeasureCode);
    });

    const isTestDataset = computed<boolean>(() => 
            props.taskItemId < 0 && (EnvironmentType.Dev === serverEnvironment() || EnvironmentType.QA ===serverEnvironment()));

    const isPastDeadline = computed<boolean>(() => {
        if (!prescriptionDetails.value?.deadline)
            return false;
        return new Date() > new Date(prescriptionDetails.value.deadline);
    });

    const deadlineDisplayStyle = computed<DisplayStyle>(()=>{
        if (isPastDeadline.value)
            return DisplayStyle.Danger;
        return DisplayStyle.Warning;
    });

    const priorityInfo = computed(() =>{
        switch(compData.epaDetails?.priority)
        {
            case EpaPriorityType.Standard:
            return { text: "Standard" };

            case EpaPriorityType.Urgent:
            return { text: "Urgent", displayStyle: DisplayStyle.Danger };

            case undefined:
            return;

            default:
            return { text: "???", displayStyle: DisplayStyle.Warning };
        }
    });    

</script>

<template>
    <TitledRow title="Patient">
        <ToggleIcon v-model:folded="compData.hidePatientDetails"/><span style="margin-right:0.25em;"></span>
        <ChoicePatientName :patientEntity="compData.patientEntity" /> 
        <MessageText v-if="prescriptionDetails?.patientId" :displayStyle="TextDisplayStyle.Muted">
            <small>
                Chart:
            </small>
        </MessageText>
        <OpenPatientButton v-if="compData.patientEntity?.entityId" :patientEntityId="compData.patientEntity?.entityId">
            <template #button="{funcOpenPatient}">
                <span @click="funcOpenPatient()" class="pointer" title="Open patient in EMR">
                    {{ prescriptionDetails?.patientId }}
                    <Icon :icon="IconTypes.User"  />
                </span>
            </template>
        </OpenPatientButton>
        
        <div v-if="!compData.hidePatientDetails" style="margin-left:2em">
            <EntityPhone :entityInfo="compData.patientEntity" />
        </div>
    </TitledRow>

    <TitledRow title="Medication">
        {{ prescriptionDetails?.medication }}, {{ prescriptionDetails?.quantity }} {{ compData.dispenseInfo?.shortDescription }} 
        <MessageText v-if="prescriptionDetails?.daysSupply" 
                :displayStyle="TextDisplayStyle.Muted">
            ({{ prescriptionDetails.daysSupply }} days)
        </MessageText>
    </TitledRow>

    <TitledRow title="Prescriber">
        {{ prescriptionDetails?.prescriberFirstName }} {{ prescriptionDetails?.prescriberLastName }}
        <MessageText :displayStyle="TextDisplayStyle.Muted">
            (NPI: {{ prescriptionDetails?.prescriberNpi }})
        </MessageText> 
    </TitledRow>        

    <TitledRow title="Reference Number" v-if="false">
        {{ prescriptionDetails?.referenceNumber }}
    </TitledRow>

    <TitledRow title="Priority" v-if="priorityInfo">
        <MessageText :displayStyle="priorityInfo.displayStyle">
            {{ priorityInfo.text }}
        </MessageText>
    </TitledRow>

    <TitledRow title="Deadline" v-if="prescriptionDetails?.deadline">
        <Icon :icon="IconTypes.Warning" :displayStyle="deadlineDisplayStyle" />
        {{ prettyDate(prescriptionDetails.deadline) }}, {{prettyTime(prescriptionDetails.deadline, false, false, true)}}

        <MessageBadge v-if="isPastDeadline" :displayStyle="DisplayStyle.Danger">
            Past deadline
        </MessageBadge>
    </TitledRow>
</template>